import {
  Activity,
  AlertTriangle,
  BarChart3,
  Building2,
  CalendarClock,
  Car,
  Coins,
  Database,
  FileText,
  Flag,
  Fuel,
  LayoutDashboard,
  Megaphone,
  ListChecks,
  Newspaper,
  Send,
  Settings,
  Tags,
  Zap,
} from 'lucide-react';
import type { Permission } from '@insider/shared';

/**
 * Navigation model.
 *
 * Kept deliberately short: sub-pages live behind tabs inside a section rather
 * than each getting a sidebar entry, so the sidebar stays scannable.
 */
export interface NavItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Hide the entry entirely when the role lacks this permission. */
  permission?: Permission;
  /** Match nested routes as active (e.g. /real-estate/:id). */
  matchPrefix?: string;
}

export interface NavGroup {
  label: string | null;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    label: null,
    items: [{ label: 'Overview', to: '/', icon: LayoutDashboard }],
  },
  {
    label: 'Data',
    items: [
      { label: 'Fuel', to: '/fuel', icon: Fuel, matchPrefix: '/fuel' },
      { label: 'Currency', to: '/currency', icon: Coins, matchPrefix: '/currency' },
      { label: 'Discounts', to: '/discounts', icon: Tags, matchPrefix: '/discounts' },
      { label: 'Tariffs', to: '/tariffs', icon: Zap, matchPrefix: '/tariffs' },
      { label: 'Real Estate', to: '/real-estate', icon: Building2, matchPrefix: '/real-estate' },
      { label: 'Vehicles', to: '/vehicles', icon: Car, matchPrefix: '/vehicles' },
    ],
  },
  {
    label: 'Content',
    items: [
      { label: 'Posts', to: '/posts', icon: FileText, matchPrefix: '/posts', permission: 'posts:read' },
      { label: 'Articles', to: '/articles', icon: Newspaper, matchPrefix: '/articles', permission: 'articles:read' },
      { label: 'Schedule', to: '/schedule', icon: CalendarClock, matchPrefix: '/schedule', permission: 'posts:read' },
    ],
  },
  {
    label: 'Audience',
    items: [{ label: 'Site analytics', to: '/analytics', icon: BarChart3, matchPrefix: '/analytics' }],
  },
  {
    label: 'Revenue',
    items: [{ label: 'Advertising', to: '/ads', icon: Megaphone, matchPrefix: '/ads' }],
  },
  {
    label: 'Telegram',
    items: [
      { label: 'Channel', to: '/telegram', icon: Send, matchPrefix: '/telegram', permission: 'telegram:read' },
      {
        label: 'Analytics',
        to: '/telegram/analytics',
        icon: BarChart3,
        matchPrefix: '/telegram/analytics',
        permission: 'telegram:read',
      },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Sources', to: '/system/sources', icon: Database, permission: 'system:read' },
      { label: 'Jobs', to: '/system/jobs', icon: Activity, permission: 'system:read' },
      { label: 'Runs', to: '/system/scrape-runs', icon: ListChecks, permission: 'system:read' },
      { label: 'Errors', to: '/system/errors', icon: AlertTriangle, permission: 'system:read' },
      { label: 'Reports', to: '/system/reports', icon: Flag, permission: 'system:read' },
    ],
  },
  {
    label: null,
    items: [{ label: 'Settings', to: '/settings', icon: Settings, matchPrefix: '/settings', permission: 'settings:read' }],
  },
];
